import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Mic2, Clock } from "lucide-react";
import { getRadioEpisode } from "@/lib/api/radio.functions";
import { RadioSocial } from "@/components/site/RadioSocial";
import { FacebookShareButton } from "@/components/site/FacebookShareButton";

const SITE_ORIGIN = "https://nkadimengdannylcs.com";
const DEFAULT_RADIO_OG = `${SITE_ORIGIN}/og/radio-default.jpg`;

function buildEpisodeShareCaption(ep: any): string {
  const title = ep?.title ?? "NDL Justice Radio episode";
  const topic = (ep?.topic ?? "").trim();
  const script = (ep?.script ?? "").trim();
  const paragraphs = script
    ? script.split(/\n{2,}/).map((p: string) => p.trim()).filter(Boolean)
    : [];
  const preview = paragraphs.slice(0, 3).join("\n\n");
  const parts = [
    `🎙️ ${title} — NDL Justice Radio`,
    topic || null,
    preview,
    "Listen to the full episode and join the conversation on nkadimengdannylcs.com.",
    "#NDLJusticeRadio #SouthAfrica #LegalHelp",
  ].filter(Boolean);
  return parts.join("\n\n").slice(0, 1800);
}

export const Route = createFileRoute("/radio/$slug")({
  loader: async ({ params }) => {
    const { episode } = await getRadioEpisode({ data: { slug: params.slug } });
    if (!episode) throw notFound();
    return { episode };
  },
  head: ({ params, loaderData }) => {
    const ep: any = (loaderData as any)?.episode;
    if (!ep) return { meta: [{ title: "Radio episode" }] };
    const canonical = `${SITE_ORIGIN}/radio/${ep.slug ?? params.slug}`;
    const description = (ep.topic || ep.title || "NDL Justice Radio episode").slice(0, 200);
    const bust = ep.published_at ? new Date(ep.published_at).getTime() : undefined;
    const ogImage = bust ? `${DEFAULT_RADIO_OG}?v=${bust}` : DEFAULT_RADIO_OG;
    const slug = ep.slug ?? params.slug;
    const audioUrl = `${SITE_ORIGIN}/api/public/radio/${slug}.mp3`;
    const hasAudio = Boolean(ep.audio_url);
    return {
      meta: [
        { title: `${ep.title} | NDL Justice Radio` },
        { name: "description", content: description },
        { property: "og:title", content: ep.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "music.song" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: "NDL Justice Radio" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `NDL Justice Radio — ${ep.title}` },
        ...(hasAudio
          ? [
              { property: "og:audio", content: audioUrl },
              { property: "og:audio:secure_url", content: audioUrl },
              { property: "og:audio:type", content: "audio/mpeg" },
              { property: "music:duration", content: String(ep.duration_sec ?? 0) },
            ]
          : []),
        { name: "twitter:card", content: "player" },
        { name: "twitter:title", content: ep.title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        ...(hasAudio
          ? [
              { name: "twitter:player:stream", content: audioUrl },
              { name: "twitter:player:stream:content_type", content: "audio/mpeg" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  notFoundComponent: () => (
    <div className="container-narrow py-16 text-center">
      <p className="text-sm text-muted-foreground">Episode not found.</p>
      <Link to="/radio" className="mt-3 inline-block text-navy underline">
        Back to NDL Justice Radio
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="container-narrow py-16 text-center">
      <p className="text-sm text-rose-600">Could not load this episode.</p>
    </div>
  ),
  component: RadioEpisodePage,
});

function fmtDuration(sec?: number | null) {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function RadioEpisodePage() {
  const { episode } = Route.useLoaderData() as any;
  const published = episode.published_at
    ? new Date(episode.published_at).toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  const duration = fmtDuration(episode.duration_sec);

  return (
    <article className="container-narrow max-w-3xl py-10">
      <Link
        to="/radio"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to NDL Justice Radio
      </Link>

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
        <Mic2 className="h-3.5 w-3.5" /> Talk session
      </div>
      <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">
        {episode.title}
      </h1>
      <p className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {published && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> Published {published}
          </span>
        )}
        {duration && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {duration}
          </span>
        )}
      </p>

      <p className="mt-5 text-base leading-relaxed text-foreground/80">
        {episode.topic}
      </p>

      {episode.audio_signed_url ? (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Listen to the full episode
          </p>
          <audio
            controls
            preload="metadata"
            src={episode.audio_signed_url}
            className="mt-3 w-full"
          >
            Your browser does not support audio playback.
          </audio>
          <a
            href={episode.audio_signed_url}
            download
            className="mt-3 inline-block text-xs text-navy underline hover:text-gold"
          >
            Download audio
          </a>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Stable public MP3 URL (shareable on Facebook / WhatsApp / LinkedIn):{" "}
            <a
              className="text-navy underline"
              href={`${SITE_ORIGIN}/api/public/radio/${episode.slug ?? episode.id}.mp3`}
              target="_blank"
              rel="noopener noreferrer"
            >
              /api/public/radio/{episode.slug ?? episode.id}.mp3
            </a>
          </p>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">
          Audio for this episode isn't available yet — the full transcript is
          below.
        </div>
      )}

      <section className="mt-8">
        <h2 className="font-display text-xl text-navy">Join the conversation</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          React, share to your network, and add a comment.
        </p>
        <div className="mt-3">
          <FacebookShareButton
            url={`${SITE_ORIGIN}/radio/${episode.slug ?? episode.id}`}
            caption={buildEpisodeShareCaption(episode)}
            itemId={episode.id}
            itemSlug={episode.slug ?? episode.id}
            kind="news"
            campaign="radio_share"
          />
        </div>
        <RadioSocial
          itemId={episode.id}
          itemKind="episode"
          shareText={`Listening to "${episode.title}" on NDL Justice Radio.`}
          shareUrl={`${SITE_ORIGIN}/radio/${episode.slug ?? episode.id}`}
        />
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl text-navy">Full transcript</h2>
        <div className="prose prose-sm mt-3 max-w-none whitespace-pre-wrap text-foreground/90">
          {episode.script}
        </div>
      </section>
    </article>
  );
}
